import { buttonStyle } from 'components/buttons/buttonStyle';
import { CREATE_DIALOG_ACTION_CLASS } from 'components/buttons/CancelButton';
import { CREATE_DIALOG_MAIN_ACTION_CLASS } from 'components/buttons/SaveButton';
import type { SvgIconComponent } from 'components/icons';
import type { ModalClassNames } from 'components/modals/ModalProvider';
import { ModalActions, useModal } from 'components/modals/useModal';
import { SectionActionsProvider, useSectionActions } from 'components/section-menu/SectionActionsContext';
import { useIsNewDesign } from 'components/tables/list-table/filter-bar/useIsNewDesign';
import { Button } from 'components/ui/button';
import { useIsMobile } from 'hooks/useIsMobile';
import { ReactNode, useCallback, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FormattedMessage } from 'react-intl';
import { cn } from 'utils/cn';
import { CreateDialogErrorSlotProvider, CreateDialogFooter, CreateDialogSheetActions } from './CreateDialogFooter';
import { CREATE_DIALOG_BODY_CLASS, CreateDialogHeader } from './CreateDialogFrame';
import { CreateDialogSections } from './CreateDialogSection';
import { focusFirstEmptyRequired } from './focusFirstEmptyRequired';

/** The modal's card at the create dialog's place and width, and the full-screen sheet under 768px. */
const CREATE_DIALOG_PANEL: ModalClassNames = {
    panel: cn(
        'top-[max(4dvh,16px)] max-h-[calc(100dvh-2*max(4dvh,16px))] w-[720px] translate-y-0 rounded-xl',
        'max-sm:top-0 max-sm:h-full max-sm:max-h-none max-sm:w-full max-sm:max-w-none max-sm:rounded-none'
    ),
};

/**
 * `useModal` for a record's own modal form: in the new design, where the form draws itself in
 * `CreateDialogModalFrame`, the modal opens at the create dialog's place and width; in classic as the caller styles it.
 */
export function useCreateDialogLookModal(): ModalActions {
    const { openModal, closeModal } = useModal();
    const isNewDesign = useIsNewDesign();
    const open = useCallback<ModalActions['openModal']>(
        (content, onCancel, style, container, classNames) =>
            isNewDesign
                ? openModal(content, onCancel, undefined, container, CREATE_DIALOG_PANEL)
                : openModal(content, onCancel, style, container, classNames),
        [isNewDesign, openModal]
    );

    return { openModal: open, closeModal };
}

/** Opens the section titled `title` beside Required, as a form asks for one of its sections. */
function OpenSection({ title }: { title?: string }) {
    const { requestExpandSection } = useSectionActions();

    useEffect(() => {
        if (title) {
            requestExpandSection(title);
        }
    }, [title, requestExpandSection]);

    return null;
}

type MainAction = { label: ReactNode; onClick: () => void; disabled?: boolean };

type Props = {
    icon: SvgIconComponent;
    tileClass: string;
    title: ReactNode;
    /** Which record an edit is of, or where a new one goes. */
    chip?: ReactNode;
    /** Above the sections: what holds for every field, such as the currency the amounts are in. */
    notice?: ReactNode;
    /** The section, by its title, that opens with Required: the one the form is mostly about. */
    openSection?: string;
    /** Between the sections and the actions: what the form says of its actions. */
    caption?: ReactNode;
    onCancel: () => void;
    cancelDisabled?: boolean;
    /** Between Cancel and the main action: Reset to original, Delete. */
    otherActions?: ReactNode;
    /** None where the user may only look. */
    mainAction?: MainAction;
    /** The form's fields, in `CreateDialogSection`s. */
    children: ReactNode;
};

/**
 * A record's own modal form in the create dialog's look, without becoming a creation: the dialog's header, the
 * fields in its sections (Required first and open, the form's others as accordions) and its footer with the
 * form's actions, which on the sheet fold as the create dialog's do. No Minimise, no Create another, no draft. It is the content of a modal opened with
 * `useCreateDialogLookModal`, whose Close and Escape cancel it. The first required field left empty takes the focus.
 */
export function CreateDialogModalFrame({
    icon,
    tileClass,
    title,
    chip,
    notice,
    openSection,
    caption,
    onCancel,
    cancelDisabled,
    otherActions,
    mainAction,
    children,
}: Props) {
    const bodyRef = useRef<HTMLDivElement>(null);
    const [footerSlot, setFooterSlot] = useState<HTMLElement | null>(null);
    const [errorSlot, setErrorSlot] = useState<HTMLElement | null>(null);
    const isSheet = useIsMobile();

    const main = mainAction ? (
        <Button
            {...buttonStyle({ variant: 'contained', color: 'secondary' }, CREATE_DIALOG_MAIN_ACTION_CLASS)}
            onClick={mainAction.onClick}
            disabled={mainAction.disabled}
        >
            {mainAction.label}
        </Button>
    ) : null;
    // On the sheet, as in the create dialog, the main action takes the row and Reset or Delete fold into a menu.
    const actions = isSheet ? (
        <CreateDialogSheetActions others={otherActions} main={main} moreLabelId="create-dialog.actions.more-actions" />
    ) : (
        <>
            <Button
                {...buttonStyle({ variant: 'outlined' }, CREATE_DIALOG_ACTION_CLASS)}
                onClick={onCancel}
                disabled={cancelDisabled}
            >
                <FormattedMessage id="cancel" />
            </Button>
            {otherActions}
            {main}
        </>
    );

    // On a timer, as the create dialog does, so the modal's own first focus is in before a field is picked.
    useEffect(() => {
        const timer = setTimeout(() => {
            if (bodyRef.current) {
                focusFirstEmptyRequired(bodyRef.current);
            }
        });
        return () => clearTimeout(timer);
    }, []);

    return (
        <>
            <CreateDialogHeader icon={icon} tileClass={tileClass} title={title} chip={chip} />
            <div ref={bodyRef} className={CREATE_DIALOG_BODY_CLASS}>
                {notice ? <div className="px-6 pt-4 max-sm:px-4">{notice}</div> : null}
                <CreateDialogErrorSlotProvider value={errorSlot}>
                    <SectionActionsProvider>
                        <OpenSection title={openSection} />
                        <CreateDialogSections>{children}</CreateDialogSections>
                    </SectionActionsProvider>
                </CreateDialogErrorSlotProvider>
            </div>
            {caption ? (
                <p className="border-neutral-tint text-caption text-foreground-tertiary m-0 shrink-0 border-t px-6 py-3 max-sm:px-4">
                    {caption}
                </p>
            ) : null}
            <CreateDialogFooter slotRef={setFooterSlot} errorSlotRef={setErrorSlot} />
            {footerSlot ? createPortal(actions, footerSlot) : null}
        </>
    );
}

/** The look of an action between Cancel and the main one, such as Reset to original. */
export const CREATE_DIALOG_OTHER_ACTION = buttonStyle({ variant: 'outlined' }, CREATE_DIALOG_ACTION_CLASS);
